/**
 * «Verificar transacción» con Banesco desde el cobro (spec 19, pedido del usuario 2026-10-23). En cada línea de pago
 * móvil o transferencia, el cajero abre el diálogo, escribe lo que le da el cliente (banco, celular, referencia, fecha) y
 * el servidor lo consulta en el banco. De lo que el banco encuentra, solo se puede elegir un abono a la cuenta de la tienda
 * que no se haya usado: al elegirlo, la línea toma el monto y la referencia que confirmó el banco y queda «verificada con
 * Banesco» (el servidor vuelve a comprobar todo al cobrar y la consume una sola vez).
 *
 * Qué búsqueda del manual se usa lo decide `modalidadBanesco` (la misma regla del servidor):
 *  - pago móvil con referencia → últimos 6 dígitos + celular + fecha + banco;
 *  - pago móvil sin referencia → celular + fecha + monto;
 *  - transferencia de Banesco a Banesco → la referencia completa o sus últimos 6 (sin fecha);
 *  - transferencia de otro banco → últimos 6 dígitos + fecha + banco (la cuenta de la tienda la pone el servidor).
 * Siempre 6 dígitos o más: con 4 el banco devuelve esos 4 y no se sabría qué pago es (ambiente de pruebas, 2026-10-23).
 */
import {
  BANCOS_VE,
  CODIGO_BANESCO,
  diaLegible,
  fechaCaracas,
  METODO_PAGO_ETIQUETA,
  modalidadBanesco,
  Money,
  type BanescoEstadoDTO,
  type MetodoVerificable,
  type TransaccionBancoDTO,
  type VerificacionBancoDTO,
} from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, Landmark, Search, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';

/** Si el servidor tiene la conexión con Banesco (no cambia mientras corre: se pregunta una vez cada 5 minutos). */
export const useBanescoEstado = () => useQuery({ queryKey: ['banesco', 'estado'], queryFn: () => get<BanescoEstadoDTO>('/banesco/estado'), staleTime: 5 * 60_000 });

const bsDe = (v: string) => `Bs ${Money.parse(v).toDisplay()}`;
const DIAS_ATRAS = 30;
/** El banco da el celular como 584143775031: se muestra como lo dice el cliente (0414-3775031). */
const telefonoLegible = (t: string | null) => {
  const m = t ? /^58(\d{3})(\d{7})$/.exec(t) : null;
  return m ? `0${m[1]}-${m[2]}` : t;
};

/**
 * Debajo de la línea de pago móvil o transferencia: el botón «Verificar transacción» o, ya verificada, lo que confirmó
 * el banco con «Quitar» (la línea vuelve a ser manual).
 */
export function LineaBanesco({ indice, metodo, monto, referencia, verificacion, otrasVerificaciones, alElegir, alQuitar }: {
  indice: number;
  metodo: MetodoVerificable;
  monto: string;
  referencia: string;
  verificacion: TransaccionBancoDTO | null | undefined;
  /** Verificaciones ya elegidas en otras líneas de este cobro (no se puede usar dos veces la misma). */
  otrasVerificaciones: string[];
  alElegir: (t: TransaccionBancoDTO) => void;
  alQuitar: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  if (verificacion) {
    return (
      <div className="col-span-full -mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-950" data-testid={`banesco-verificada-${indice}`}>
        <CircleCheck className="h-4 w-4 shrink-0 text-emerald-700" />
        <b>Verificado con Banesco</b>
        <span className="num font-semibold">{bsDe(verificacion.monto)}</span>
        <span>ref. {verificacion.referencia}{verificacion.fecha ? ` · ${diaLegible(verificacion.fecha)}` : ''}{verificacion.hora ? ` ${verificacion.hora}` : ''}{verificacion.banco_origen_nombre ? ` · desde ${verificacion.banco_origen_nombre}` : ''}</span>
        <Button type="button" size="sm" variant="ghost" className="ml-auto h-7 px-2 text-xs" onClick={alQuitar} data-testid={`banesco-quitar-${indice}`}>
          <X className="h-3.5 w-3.5" /> Quitar
        </Button>
      </div>
    );
  }
  return (
    <>
      <div className="col-span-full -mt-1">
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAbierto(true)} data-testid={`banesco-verificar-${indice}`}>
          <Landmark className="h-3.5 w-3.5" /> Verificar transacción
        </Button>
      </div>
      {abierto ? (
        <VerificarBanescoDialog
          metodo={metodo}
          monto={monto}
          referencia={referencia}
          otrasVerificaciones={otrasVerificaciones}
          cerrar={() => setAbierto(false)}
          alElegir={(t) => {
            alElegir(t);
            setAbierto(false);
          }}
        />
      ) : null}
    </>
  );
}

export function VerificarBanescoDialog({ metodo, monto, referencia, otrasVerificaciones, cerrar, alElegir }: {
  metodo: MetodoVerificable;
  monto: string;
  referencia: string;
  otrasVerificaciones: string[];
  cerrar: () => void;
  alElegir: (t: TransaccionBancoDTO) => void;
}) {
  const estado = useBanescoEstado();
  const hoy = fechaCaracas(new Date());
  const minimo = fechaCaracas(new Date(Date.parse(`${hoy}T12:00:00Z`) - DIAS_ATRAS * 86_400_000));
  const [banco, setBanco] = useState('');
  const [telefono, setTelefono] = useState('');
  const [ref, setRef] = useState(referencia.replace(/\D/g, ''));
  const [fecha, setFecha] = useState(hoy);
  const [montoBs, setMontoBs] = useState(parseMonto(monto)?.esPositivo() ? monto : '');
  const [consultando, setConsultando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<VerificacionBancoDTO | null>(null);
  const [intentado, setIntentado] = useState(false);

  const montoValido = parseMonto(montoBs)?.esPositivo() ? normalizarDecimal(montoBs) : undefined;
  const datos = { metodo, banco_origen: banco, fecha, referencia: ref || undefined, telefono: telefono || undefined, monto: montoValido };
  const plan = banco ? modalidadBanesco(datos) : ({ campo: 'banco_origen', error: 'Elige el banco desde donde pagó el cliente' } as const);
  const errorDe = (campo: string) => (intentado && 'error' in plan && plan.campo === campo ? plan.error : undefined);
  const configurado = !!estado.data?.configurado;
  const esBanesco = banco === CODIGO_BANESCO;
  const pideTelefono = metodo === 'pago_movil';
  const placeholderRef = metodo === 'transferencia' && esBanesco ? 'Completa o sus últimos 6 dígitos' : metodo === 'pago_movil' ? 'Últimos 6 dígitos (o escribe el monto)' : 'Últimos 6 dígitos';

  const consultar = async () => {
    setIntentado(true);
    if ('error' in plan || !configurado || consultando) return;
    setConsultando(true);
    setError(null);
    setResultado(null);
    try {
      setResultado(await post<VerificacionBancoDTO>('/banesco/verificar', { metodo, banco_origen: banco, fecha, ...(ref ? { referencia: ref } : {}), ...(pideTelefono && telefono ? { telefono } : {}), ...(montoValido ? { monto: montoValido } : {}) }));
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setConsultando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-h-[calc(100vh-1.5rem)] max-w-2xl overflow-y-auto" data-testid="dialogo-banesco">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5" /> Verificar transacción con Banesco
            {estado.data?.simulado ? <Badge variant="pendiente" data-testid="banesco-simulado">Simulador de desarrollo</Badge> : null}
          </DialogTitle>
          <DialogDescription>
            {METODO_PAGO_ETIQUETA[metodo]}: escribe los datos que te da el cliente y consulta. Solo sirve un pago que entró a la cuenta de la tienda y que no se haya usado en otro cobro.
          </DialogDescription>
        </DialogHeader>
        {estado.data && !configurado ? (
          <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="banesco-no-configurado">
            La conexión con Banesco aún no está configurada en el servidor. Verifica el pago a mano, como hasta ahora.
          </p>
        ) : null}
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            void consultar();
          }}
        >
          <Campo etiqueta="Banco desde donde pagó" htmlFor="banesco-banco" error={errorDe('banco_origen')}>
            <Select id="banesco-banco" value={banco} onChange={(e) => setBanco(e.target.value)} disabled={!configurado} data-testid="banesco-banco">
              <option value="">Elige el banco…</option>
              {BANCOS_VE.map((b) => (
                <option key={b.codigo} value={b.codigo}>{b.codigo} · {b.nombre}</option>
              ))}
            </Select>
          </Campo>
          {pideTelefono ? (
            <Campo etiqueta="Celular desde donde pagó" htmlFor="banesco-telefono" error={errorDe('telefono')}>
              <Input id="banesco-telefono" inputMode="tel" placeholder="0414-1234567" value={telefono} onChange={(e) => setTelefono(e.target.value)} disabled={!configurado} data-testid="banesco-telefono" />
            </Campo>
          ) : null}
          <Campo etiqueta="Referencia" htmlFor="banesco-referencia" error={errorDe('referencia')}>
            <Input id="banesco-referencia" inputMode="numeric" placeholder={placeholderRef} value={ref} onChange={(e) => setRef(e.target.value.replace(/\D/g, ''))} disabled={!configurado} data-testid="banesco-referencia" />
          </Campo>
          {metodo === 'transferencia' && esBanesco ? null : (
            <Campo etiqueta="Fecha del pago" htmlFor="banesco-fecha">
              <Input id="banesco-fecha" type="date" min={minimo} max={hoy} value={fecha} onChange={(e) => setFecha(e.target.value || hoy)} disabled={!configurado} data-testid="banesco-fecha" />
            </Campo>
          )}
          <Campo etiqueta="Monto en Bs" htmlFor="banesco-monto" error={errorDe('monto')}>
            <Input id="banesco-monto" inputMode="decimal" placeholder="Bs (1.234,56)" value={montoBs} onChange={(e) => setMontoBs(e.target.value)} disabled={!configurado} data-testid="banesco-monto" />
          </Campo>
          <div className="flex items-end sm:col-span-2">
            <Button type="submit" disabled={!configurado} cargando={consultando} data-testid="banesco-consultar">
              <Search /> Consultar en Banesco
            </Button>
          </div>
        </form>
        {error ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert" data-testid="banesco-error">{error}</p>
        ) : null}
        {resultado ? (
          resultado.resultados.length === 0 ? (
            <p className="rounded-lg border px-3 py-2 text-sm text-muted-foreground" data-testid="banesco-sin-resultados">
              Banesco no encontró ningún pago con esos datos. Revisa el banco, el celular, la referencia y la fecha. Si el cliente insiste, verifica el pago a mano.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="banesco-resultados">
              {resultado.resultados.map((t, k) => {
                const repetida = !!t.verificacion_id && otrasVerificaciones.includes(t.verificacion_id);
                const usable = t.elegible && !!t.verificacion_id && !repetida;
                return (
                  <li key={`${t.referencia}-${k}`} className={`rounded-lg border p-2.5 text-sm ${usable ? 'border-emerald-300' : 'bg-muted/40 text-muted-foreground'}`} data-testid={`banesco-resultado-${k}`}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className={`num text-base font-semibold ${usable ? 'text-foreground' : ''}`}>{bsDe(t.monto)}</span>
                      {t.coincide_monto === true ? <Badge variant="ok">Coincide con el monto</Badge> : t.coincide_monto === false ? <Badge variant="pendiente">Monto distinto</Badge> : null}
                      <span>ref. <b>{t.referencia}</b></span>
                      {t.fecha ? <span>{diaLegible(t.fecha)}{t.hora ? ` ${t.hora}` : ''}</span> : null}
                      {usable ? (
                        <Button type="button" size="sm" className="ml-auto" onClick={() => alElegir(t)} data-testid={`banesco-usar-${k}`}>
                          Usar este pago
                        </Button>
                      ) : null}
                    </div>
                    <p className="mt-0.5 text-xs">
                      {[t.banco_origen_nombre ? `Desde ${t.banco_origen_nombre}` : t.banco_origen && /^\d{4}$/.test(t.banco_origen) ? `Banco ${t.banco_origen}` : null, t.id_pagador, telefonoLegible(t.telefono_pagador), t.cuenta_origen, t.concepto].filter(Boolean).join(' · ')}
                    </p>
                    {!t.elegible && t.motivo ? <p className="mt-1 text-xs font-medium text-mg-rojo" data-testid={`banesco-motivo-${k}`}>{t.motivo}</p> : null}
                    {repetida ? <p className="mt-1 text-xs font-medium text-mg-rojo">Ya lo elegiste en otra línea de este cobro.</p> : null}
                    {t.aviso ? <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900" data-testid={`banesco-aviso-${k}`}>{t.aviso}</p> : null}
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={cerrar}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
